import { CategoryPathResult, ProductSearchResult, SimpleProduct } from '@msdyn365-commerce/retail-proxy';
import { ICategoryUrl } from '@msdyn365-commerce/core-internal/dist/types/interfaces/ICategoryPathInterfaces';
import { getCategoryPathsAsync } from '@msdyn365-commerce/retail-proxy/dist/DataActions/ProductsDataActions.g';
import { IAny, ICoreContext, IGeneric } from '@msdyn365-commerce/core-internal';

const categoryPathsCache = new Map<number, CategoryPathResult>();

export interface IDataLayerEvent {
    event?: string;
    [key: string]: any;
}

export interface IDataLayerProductItem {
    item_id?: string;
    item_name?: string;
    price?: number;
    quantity?: number;
    delivery_type?: string;
    shipping_tier?: string;
    [key: string]: any;
}

export const pushToDataLayer = (data: IDataLayerEvent): void => {
    if (typeof window === 'undefined') {
        return;
    }

    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push(data);
};

export const getProductData = (
    product?: SimpleProduct | ProductSearchResult,
    price?: number,
    quantity?: number,
    categories?: ICategoryUrl[],
    deliveryMode?: string
): IDataLayerProductItem | undefined => {
    if (!product) {
        return;
    }
    const productData: IDataLayerProductItem = {
        item_id: product?.ItemId,
        item_name: product?.Name,
        price: price ?? product?.Price,
        quantity: quantity ?? 1
    };

    if (categories && categories.length) {
        categories.forEach((category, i) => {
            const categoryIndex = `item_category${i > 0 ? i + 1 : ''}`;
            productData[categoryIndex] = category.Name;
        });
    }

    if (deliveryMode && deliveryMode !== 'ClickCol') {
        productData.delivery_type = 'Home Delivery';
    } else {
        productData.delivery_type = 'Click & Collect';
    }

    productData.shipping_tier = deliveryMode;

    return productData;
};

export const getProductCategoriesPaths = async (productIds: number[], ctx: ICoreContext<IGeneric<IAny>>): Promise<CategoryPathResult[]> => {
    const validProductIds = productIds.filter(productId => !!productId);
    const missingProductIds = validProductIds.filter(productId => !categoryPathsCache.has(productId));

    if (missingProductIds.length === 0) {
        return validProductIds.map(productId => categoryPathsCache.get(productId)!).filter(Boolean);
    }

    try {
        const categoryPathLookup = missingProductIds.map(id => ({ ProductId: id }));
        const categoryPaths = await getCategoryPathsAsync(
            { callerContext: ctx.actionContext },
            ctx.request.channel?.RecordId || 0,
            0,
            categoryPathLookup
        );

        categoryPaths.forEach(categoryPath => {
            if (categoryPath.ProductId) {
                categoryPathsCache.set(categoryPath.ProductId, categoryPath);
            }
        });

        return validProductIds.map(productId => categoryPathsCache.get(productId)!).filter(Boolean);
    } catch (error) {
        console.error(error);
        return validProductIds.map(productId => categoryPathsCache.get(productId)!).filter(Boolean);
    }
};
